/**
 * Formatting helpers.
 *
 * Money is ALWAYS handled in integer cents, mirroring the engine — no float
 * ever touches a price. `formatMoney` is the only place cents become a string,
 * and every rendered figure is wrapped in the mono face per the design system.
 */

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const USD_WHOLE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** Integer cents -> "$45.00". */
export function formatMoney(cents: number | null | undefined): string {
  if (cents == null || Number.isNaN(cents)) return '-';
  return USD.format(cents / 100);
}

/** Integer cents -> "$45" when whole, "$45.50" otherwise. Used in dense lists. */
export function formatMoneyCompact(cents: number | null | undefined): string {
  if (cents == null || Number.isNaN(cents)) return '-';
  return cents % 100 === 0 ? USD_WHOLE.format(cents / 100) : USD.format(cents / 100);
}

/**
 * Event dates arrive as bare `YYYY-MM-DD` from Postgres DATE columns. Parsing
 * that with `new Date()` treats it as UTC midnight and can render the previous
 * day for anyone west of Greenwich, so build the date in local time instead.
 */
function parseEventDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDate(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
): string {
  if (!value) return 'Date TBA';
  const date = parseEventDate(value);
  if (!date) return 'Date TBA';
  return new Intl.DateTimeFormat('en-US', options).format(date);
}

export function formatDateShort(value: string | null | undefined): string {
  return formatDate(value, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Postgres TIME values arrive as "20:00:00". */
export function formatTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = match[2];
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${minute} ${suffix}`;
}

/** Timestamps (with time) for order history. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(parsed);
}

/** Shorten a UUID for display; the full value stays in the DOM title. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '-';
  return id.slice(0, 8).toUpperCase();
}
